import React, { Fragment, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import { Skeleton } from '@core-ui/Components/ui/skeleton.tsx';
import { useTranslation } from 'react-i18next';
import { type IntegrationJob, JobStatus } from '@features/dashboard/types/dashboard.types.ts';
import EmptyState from '@features/dashboard/components/shared/EmptyState.tsx';
import JobLogs from '@features/dashboard/components/dashboardIntegrations/JobLogs.tsx';
import { PaginationControls } from '@ui/shared/paginationControls.tsx';
import { cn } from '@core-ui-lib/utils.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { RefreshCw } from 'lucide-react';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '@core-ui/Components/ui/tooltip.tsx';

interface Props {
  jobs: IntegrationJob[] | null;
  isLoading: boolean;
  isRetrying: boolean;
  currentPage: number;
  totalCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onRetryJob: (jobId: string, includeSkipped?: boolean) => Promise<void>;
  showSyncInsightsRetry?: boolean;
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50];
const ERROR_ATTEMPT_NUMBER = 4;
const jobStatus: Record<string, string> = {
  'CREATED': 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300',
  'SUCCESS': 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300',
  'ERROR': 'bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-300',
  'PROCESSING': 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300',
  'CONFIRMED': 'bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300',
};

function HeaderWithTooltip ({
  label,
  tooltip,
  className,
}: {
  label: string;
  tooltip: string;
  className?: string;
}): React.JSX.Element {
  return (
    <TableHead className={className}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="border-b border-dotted border-muted-foreground/50">
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TableHead>
  );
}

function TableSkeleton ({ rowCount = 5 }: { rowCount?: number }): React.JSX.Element {
  const skeletonClass = 'bg-muted';
  return (
    <>
      {Array.from({ length: rowCount }).map((_, i) => (
        <TableRow key={i} className="border-b border-border/60">
          <TableCell><Skeleton className={`h-4 w-32 ${skeletonClass}`} /></TableCell>
          <TableCell><Skeleton className={`h-4 w-32 ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-6 w-20 rounded-full inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-4 w-14 inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-4 w-14 inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-4 w-14 inline-block ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center"><Skeleton className={`h-6 w-8 inline-block ${skeletonClass}`} /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

function formatDate (
  dateValue: string | { $date: string } | null | undefined,
  lang: string
): string {
  if (dateValue == null) {
    return '-';
  }

  const localeLower = (lang ?? '').toLowerCase();
  const locale =
      localeLower.startsWith('es') ? 'es-ES' :
        localeLower.startsWith('en') ? 'en-US' :
          'en-US';

  let dateString: string;
  if (typeof dateValue === 'object' && dateValue.$date !== '') {
    dateString = dateValue.$date;
  } else if (typeof dateValue === 'string') {
    dateString = dateValue;
  } else {
    return '-';
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }

  const dateStr = date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  return `${dateStr}, ${timeStr}`;
}

export default function IntegrationJobsTable ({
  jobs,
  isLoading,
  isRetrying,
  currentPage,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRetryJob,
  showSyncInsightsRetry = false,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [retryRow, setRetryRow] = useState<number | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);

  function handleRetryClick (jobId: string, index: number): void {
    void onRetryJob(jobId);
    setRetryRow(index);
    setTimeout(() => {
      setRetryRow(null);
    }, 1500);
  }

  const handleSyncInsightsRetry = async (jobId: string, includeSkipped: boolean): Promise<void> => {
    setRetryingJobId(jobId);
    try {
      await onRetryJob(jobId, includeSkipped);
    } finally {
      setRetryingJobId(null);
    }
  };

  return (
    <div className="w-full overflow-auto rounded-md border border-border bg-card">
      <Table>
        <TableHeader className="bg-muted/30">
          <TableRow className="border-b border-border/60 hover:bg-transparent">
            <TableHead className="text-muted-foreground font-medium">{t('dashboard.integrations.jobsTable.jobId')}</TableHead>
            <TableHead className="text-muted-foreground font-medium">{t('dashboard.integrations.jobsTable.contactList')}</TableHead>
            <HeaderWithTooltip
              className="text-muted-foreground font-medium text-center"
              label={t('dashboard.integrations.jobsTable.status')}
              tooltip={t('dashboard.integrations.jobsTable.tooltips.status')}
            />
            <HeaderWithTooltip
              className="text-muted-foreground font-medium text-center"
              label={t('dashboard.integrations.jobsTable.attempt')}
              tooltip={t('dashboard.integrations.jobsTable.tooltips.attempt')}
            />
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.lastActivity')}</TableHead>
            <TableHead className="text-muted-foreground font-medium text-center">{t('dashboard.integrations.jobsTable.created')}</TableHead>
            <HeaderWithTooltip
              className="text-muted-foreground font-medium text-center"
              label={t('dashboard.integrations.jobsTable.actions')}
              tooltip={t('dashboard.integrations.jobsTable.tooltips.actions')}
            />
          </TableRow>
        </TableHeader>

        {(jobs?.length === 0 || jobs === null) && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={7}>
                <div className="flex justify-center items-center min-h-[200px]">
                  <EmptyState message={t('dashboard.integrations.jobsTable.empty')} />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {isLoading ? (
          <TableSkeleton />
        ) : (
          <TableBody>
            {jobs?.map((job, i) => {
              const hasSyncInsights =
                (job.syncErrors?.length ?? 0) > 0 ||
                (job.counts != null &&
                  (job.counts.failed > 0 || job.counts.skipped > 0 || job.counts.success > 0));
              const hasFailedSync = (job.counts?.failed ?? 0) > 0;
              const showActionRetry =
                (job.status as JobStatus) === JobStatus.ERROR ||
                job.attempt === ERROR_ATTEMPT_NUMBER ||
                hasFailedSync;

              return (
                <Fragment key={job._id}>
                  <TableRow className="cursor-pointer border-b border-border/60 hover:bg-muted/50 transition-colors">
                    <TableCell>{job._id}</TableCell>
                    <TableCell>{job.contactListId}</TableCell>
                    <TableCell className="text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className={cn('text-xs px-2 py-0.5 capitalize border', jobStatus[job.status])}>
                            {t(`dashboard.integrations.jobsTable.jobStatus.${job.status}`)}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px]">
                          {t(`dashboard.integrations.jobsTable.tooltips.jobStatus.${job.status}`, {
                            defaultValue: t('dashboard.integrations.jobsTable.tooltips.status'),
                          })}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-center">{job.attempt}</TableCell>
                    <TableCell className="text-center">{formatDate(job.updatedAt, i18n.language)}</TableCell>
                    <TableCell className="text-center">{formatDate(job.createdAt, i18n.language)}</TableCell>
                    <TableCell className="text-center">
                      {showActionRetry && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => {
                                handleRetryClick(job._id, i);
                              }}
                              disabled={retryRow === i && isRetrying}
                              aria-label={t('dashboard.integrations.jobsTable.retry')}
                            >
                              <RefreshCw className="w-4 h-4 text-sky-500 hover:text-sky-800"/>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[260px]">
                            {t('dashboard.integrations.jobsTable.tooltips.retry')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {hasSyncInsights && (
                    <TableRow className="hover:bg-transparent border-b border-border/60">
                      <TableCell colSpan={7} className="p-0">
                        <JobLogs
                          job={job}
                          showRetry={showSyncInsightsRetry}
                          isRetrying={retryingJobId === job._id || (isRetrying && retryingJobId === job._id)}
                          onRetry={handleSyncInsightsRetry}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        )}
      </Table>

      <PaginationControls
        mode="server"
        totalItems={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        isLoading={isLoading}
        className="border-t border-border/60"
      />
    </div>
  );
}
